import React, { useMemo, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  Compass,
  Scales,
  Lightning,
  ShieldCheck,
  ArrowRight,
  ArrowDown,
  CurrencyDollar,
  ChartLineUp,
  Sparkle,
  UploadSimple,
  FileCsv,
  CheckCircle,
  Crosshair,
  Users,
  Brain,
  Robot,
  Pulse,
  ChartBar,
  TrendUp,
  Buildings,
  Briefcase,
  X,
} from "@phosphor-icons/react";

/* ─────────────────────────────────────────────────────────────────────────
   Procureo · Boardroom Landing Page
   Strict palette:
     Alabaster      #FAF9F6   (surface)
     Deep Slate     #0F172A   (ink + headlines)
     Tiffany Blue   #0D9488   (Procureo brand signals · "pop")
     Executive Green #0D9488  (compliance · sign-off · success markers)
   ─────────────────────────────────────────────────────────────────────── */

const ALABASTER = "#FAF9F6";
const DEEP_SLATE = "#0F172A";
const TIFFANY   = "#0D9488";
const EX_GREEN  = "#0D9488";
const RULE      = "#E5E7EB";
const SUBTEXT   = "#334155";

/* ─── Engine cards · OODA loop · ZERO Risk-Score / CRS jargon ───────────── */
const ENGINE = [
  {
    stage: "OBSERVE",
    icon: Eye,
    title: "Observe",
    body:
      "Continuous ingestion of spend, market indices, and supplier signals. One live operating picture across every category line.",
  },
  {
    stage: "ORIENT",
    icon: Compass,
    title: "Orient",
    body:
      "Each opportunity is graded with PDI (Procurement Decision Intelligence). One inverted 100-point score. Higher is better.",
  },
  {
    stage: "DECIDE",
    icon: Scales,
    title: "Decide",
    body:
      "Strategy is composed against decision-adjusted value, sourcing levers, and counter-party posture. The output is surfaced for board approval.",
  },
  {
    stage: "ACT",
    icon: Lightning,
    title: "Act",
    body:
      "Awards, refinements, and signed decisions are written back to the Performance Ledger. Reconciliation runs in real time.",
  },
  {
    stage: "LEARN",
    icon: Brain,
    title: "Learn",
    body:
      "Every signed decision writes back to the Performance Ledger. Outcomes against predictions sharpen the next cycle. The system gets smarter the longer it runs.",
  },
];

export default function Landing() {
  return (
    <main
      data-testid="landing-page"
      className="min-h-screen w-full font-inter"
      style={{ background: ALABASTER, color: DEEP_SLATE }}
    >
      <BrandBar />
      <PulseTicker />
      <Hero />
      <CfoStrip />
      <Engine />
      <StrategicOverlay />
      <BuyerUserSplit />
      <StrategicIntelligence />
      <CompetitivePositioning />
      <IntelligenceCrew />
      <NegotiationSupremacy />
      <CounterpartyResilience />
      <WorkingCapitalRelease />
      <SignOffGate />
      <PersonnelBoard />
      <AuditHook />
      <Calculator />
      <Ascension />
      <Footer />
    </main>
  );
}

/* ─── 1 · Brand bar — perfectly horizontal, single clean line ───────────── */
function BrandBar() {
  return (
    <header
      data-testid="landing-brand-bar"
      className="sticky top-0 z-30 backdrop-blur"
      style={{ background: `${ALABASTER}EE`, borderBottom: `1px solid ${RULE}` }}
    >
      <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between gap-6">
        <div
          data-testid="landing-brand-block"
          className="flex items-baseline gap-3 whitespace-nowrap"
        >
          <span
            className="font-serif-brand text-[24px] font-extrabold tracking-[0.02em]"
            style={{ color: EX_GREEN }}
            data-testid="landing-brand-wordmark"
          >
            PROCUREO
          </span>
          <span
            aria-hidden
            className="inline-block h-4 w-px"
            style={{ background: "#94A3B8" }}
          />
          <span
            className="text-[12px] font-semibold tracking-[0.18em] uppercase"
            style={{ color: SUBTEXT }}
          >
            Your Strategic Business Partner
          </span>
        </div>

        <Link
          to="/command"
          data-testid="landing-enter-command"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[11px] font-mono tracking-[0.14em] uppercase font-bold transition-colors"
          style={{
            background: DEEP_SLATE,
            color: ALABASTER,
            border: `1px solid ${DEEP_SLATE}`,
          }}
        >
          Enter Command Center <ArrowRight weight="bold" size={12} />
        </Link>
      </div>
    </header>
  );
}

/* ─── 2 · Hero — boardroom headline + CTA ───────────────────────────────── */
function Hero() {
  const [briefingOpen, setBriefingOpen] = useState(false);
  return (
    <section
      data-testid="landing-hero"
      className="max-w-7xl mx-auto px-8 pt-20 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10"
    >
      <div className="lg:col-span-8">
        <div
          className="text-[11px] font-mono tracking-[0.22em] uppercase mb-6"
          style={{ color: EX_GREEN }}
          data-testid="landing-eyebrow"
        >
          Procureo v0.2 · Common Operating Picture
        </div>

        <h1
          className="font-serif-brand font-semibold leading-[1.05] text-[44px] sm:text-[58px] lg:text-[72px]"
          style={{ color: DEEP_SLATE, letterSpacing: "-0.02em" }}
          data-testid="landing-headline"
        >
          <span className="font-serif-brand font-extrabold" style={{ color: EX_GREEN, letterSpacing: "0.01em" }} data-testid="landing-headline-brand">PROCUREO</span> turns procurement into a{" "}
          <span style={{ color: EX_GREEN }}>competitive weapon</span> that protects your topline.
        </h1>

        <p
          className="mt-7 max-w-2xl text-[17px] leading-relaxed"
          style={{ color: SUBTEXT }}
          data-testid="landing-subhead"
        >
          Procureo is not software you subscribe to and hope to use. It is a Results-as-a-Service partner that works alongside your procurement team — surfacing which deals to renegotiate, which renewals are leaking margin, which suppliers to protect against — every finding dollar-attributed and defensible to the board.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => setBriefingOpen(true)}
            data-testid="landing-cta-briefing"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-[12px] font-mono tracking-[0.16em] uppercase font-bold transition-transform hover:-translate-y-px"
            style={{
              background: EX_GREEN,
              color: ALABASTER,
              border: `1px solid ${EX_GREEN}`,
            }}
          >
            Request a Briefing
            <ArrowRight weight="bold" size={13} />
          </button>

          <a
            href="#value-calculator"
            data-testid="landing-cta-primary"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-[12px] font-mono tracking-[0.16em] uppercase font-bold transition-transform hover:-translate-y-px"
            style={{
              background: DEEP_SLATE,
              color: ALABASTER,
              border: `1px solid ${DEEP_SLATE}`,
            }}
          >
            Quantify Your Competitive Advantage
            <ArrowDown weight="bold" size={13} />
          </a>

          <Link
            to="/command"
            data-testid="landing-cta-secondary"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-[12px] font-mono tracking-[0.16em] uppercase font-bold transition-colors"
            style={{
              background: "transparent",
              color: DEEP_SLATE,
              border: `1px solid ${DEEP_SLATE}`,
            }}
          >
            See the Operating Picture
            <ArrowRight weight="bold" size={13} />
          </Link>
        </div>
      </div>

      {/* Heritage proof column */}
      <aside
        className="lg:col-span-4 self-end"
        data-testid="landing-proof-card"
      >
        <div
          className="p-6 rounded-sm"
          style={{ background: "#FFFFFF", border: `1px solid ${RULE}` }}
        >
          <div
            className="text-[10px] font-mono tracking-[0.22em] uppercase mb-4"
            style={{ color: SUBTEXT }}
          >
            Design Partner Outcomes · Modeled
          </div>
          <ProofRow label="Reinvestment Capital secured" value="$48.2M" />
          <ProofRow label="Revenue Equivalence" value="$612M" highlight />
          <ProofRow label="PDI Coverage of strategic spend" value="94" />
          <ProofRow label="Decision-adjusted sign-offs" value="180 awards" />
          <div
            className="mt-5 pt-4 text-[11px] leading-relaxed"
            style={{ color: SUBTEXT, borderTop: `1px solid ${RULE}` }}
          >
            Indicative outcomes from design-partner engagements. Productized figures will publish at GA.
          </div>
          <div
            className="mt-2 text-[11px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            Every figure is reconciled against the source-of-truth spend ledger before it leaves this surface.
          </div>
        </div>
      </aside>

      <BriefingModal open={briefingOpen} onClose={() => setBriefingOpen(false)} />
    </section>
  );
}

function ProofRow({ label, value, highlight = false }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <div className="text-[12px]" style={{ color: SUBTEXT }}>
        {label}
      </div>
      <div
        className="font-mono font-bold text-[18px]"
        style={{ color: highlight ? EX_GREEN : DEEP_SLATE }}
      >
        {value}
      </div>
    </div>
  );
}

/* ─── 3 · CFO Insurance Badge strip — Executive Green only ─────────────── */
function CfoStrip() {
  return (
    <section
      data-testid="landing-cfo-strip"
      className="max-w-7xl mx-auto px-8 pb-10"
    >
      <div
        className="flex flex-wrap items-center justify-between gap-6 p-5 rounded-sm"
        style={{
          background: "#FFFFFF",
          border: `1px solid ${RULE}`,
          borderLeft: `4px solid ${EX_GREEN}`,
        }}
      >
        <div className="flex items-center gap-4">
          <span
            data-testid="landing-cfo-badge"
            className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-sm font-mono text-[11px] tracking-[0.14em] uppercase font-bold"
            style={{
              background: EX_GREEN,
              color: ALABASTER,
              border: `1px solid ${EX_GREEN}`,
            }}
          >
            <ShieldCheck weight="fill" size={14} />
            CFO Insurance · Verified
          </span>
          <div
            className="text-[13px] leading-tight"
            style={{ color: DEEP_SLATE }}
          >
            <div className="font-semibold">Data integrity verified live · 11 / 11 passing</div>
            <div className="text-[12px]" style={{ color: SUBTEXT }}>
              Every output is reconcilable against the Source-of-Truth ledger before sign-off.
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-[11px] font-mono tracking-[0.16em] uppercase" style={{ color: SUBTEXT }}>
          <Sparkle weight="fill" size={12} style={{ color: EX_GREEN }} />
          Audit-grade integrity · 11/11 assertions verified
        </div>
      </div>
    </section>
  );
}

/* ─── 4 · The Engine — OODA cards, ORIENT uses PDI ─────────────────────── */
function Engine() {
  return (
    <section
      id="the-engine"
      data-testid="landing-engine"
      className="max-w-7xl mx-auto px-8 py-20"
    >
      <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
        <div>
          <div
            className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
            style={{ color: EX_GREEN }}
          >
            01 · The Engine
          </div>
          <h2
            className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
            style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
          >
            One operating loop. Five disciplined moves.
          </h2>
        </div>
        <p
          className="max-w-md text-[14px] leading-relaxed"
          style={{ color: SUBTEXT }}
        >
          Every Procureo finding runs through a disciplined OODA loop. Raw signal becomes a board-grade recommendation, every action stays signed off, and every prediction is reconciled against actual outcomes — calibrated over rolling 90-day windows so the partnership gets more accurate with every quarter.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
        {ENGINE.map((card, i) => (
          <EngineCard key={card.stage} card={card} index={i} />
        ))}
      </div>
    </section>
  );
}

function EngineCard({ card, index }) {
  const Icon = card.icon;
  return (
    <article
      data-testid={`landing-engine-card-${card.stage.toLowerCase()}`}
      className="group p-6 rounded-sm flex flex-col h-full transition-all hover:-translate-y-0.5"
      style={{ background: "#FFFFFF", border: `1px solid ${RULE}` }}
    >
      <div className="flex items-center justify-between mb-6">
        <span
          className="inline-flex items-center justify-center h-9 w-9 rounded-sm"
          style={{ background: DEEP_SLATE, color: ALABASTER }}
        >
          <Icon weight="bold" size={18} />
        </span>
        <span
          className="text-[10px] font-mono tracking-[0.22em] uppercase"
          style={{ color: SUBTEXT }}
        >
          0{index + 1} · {card.stage}
        </span>
      </div>
      <h3
        className="font-serif-brand font-semibold text-[22px] mb-3"
        style={{ color: DEEP_SLATE }}
      >
        {card.title}
      </h3>
      <p className="text-[13px] leading-relaxed" style={{ color: SUBTEXT }}>
        {card.body}
      </p>
    </article>
  );
}

/* ─── 5 · Value Calculator — Reinvestment Capital + Revenue Equivalence ── */
function Calculator() {
  const [spend, setSpend] = useState(150_000_000);   // $150M strategic spend default
  const [margin, setMargin] = useState(8);            // 8% net margin default
  const [recovery, setRecovery] = useState(8);        // 8% recovery on strategic spend

  const reinvestment = useMemo(() => Math.round(spend * (recovery / 100)), [spend, recovery]);
  const revenueEquiv = useMemo(
    () => Math.round(reinvestment / (margin / 100)),
    [reinvestment, margin]
  );

  const fmt = (n) =>
    n >= 1_000_000_000
      ? `$${(n / 1_000_000_000).toFixed(2)}B`
      : n >= 1_000_000
      ? `$${(n / 1_000_000).toFixed(1)}M`
      : `$${n.toLocaleString()}`;

  return (
    <section
      id="value-calculator"
      data-testid="landing-value-calculator"
      className="border-t"
      style={{ borderColor: RULE, background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left · controls */}
        <div className="lg:col-span-5">
          <div
            className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
            style={{ color: EX_GREEN }}
          >
            11 · Value Calculator
          </div>
          <h2
            className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight mb-5"
            style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
          >
            Quantify your competitive advantage.
          </h2>
          <p
            className="text-[14px] leading-relaxed mb-8"
            style={{ color: SUBTEXT }}
          >
            Procureo releases trapped working capital from your category portfolio. We translate the release into the metric your board already tracks. That number is the revenue you would otherwise have to win to match it.
          </p>

          <Field
            testid="calc-spend"
            label="Strategic indirect spend (annual)"
            value={fmt(spend)}
            min={10_000_000}
            max={2_000_000_000}
            step={5_000_000}
            onChange={setSpend}
            raw={spend}
          />

          <Field
            testid="calc-margin"
            label="Net margin (%)"
            value={`${margin.toFixed(1)}%`}
            min={1}
            max={30}
            step={0.5}
            onChange={setMargin}
            raw={margin}
            float
          />

          <Field
            testid="calc-recovery"
            label="Recovery rate on strategic spend (%)"
            value={`${recovery.toFixed(1)}%`}
            min={3}
            max={12}
            step={0.5}
            onChange={setRecovery}
            raw={recovery}
            float
          />

          <p
            className="text-[11px] leading-relaxed mt-2"
            style={{ color: SUBTEXT }}
            data-testid="calc-recovery-footnote"
          >
            Recovery rates of 6–10% on strategic indirect spend are typical for category-led programs in the first 12 months.
          </p>
        </div>

        {/* Right · output */}
        <div className="lg:col-span-7">
          <div
            data-testid="calc-output-card"
            className="p-8 rounded-sm h-full flex flex-col"
            style={{ background: ALABASTER, border: `1px solid ${RULE}` }}
          >
            {/* Primary · Revenue Equivalence */}
            <div className="mb-8">
              <div
                className="text-[10px] font-mono tracking-[0.22em] uppercase mb-2 flex items-center gap-2"
                style={{ color: EX_GREEN }}
              >
                <ChartLineUp weight="bold" size={12} />
                Primary Achievement · Revenue Equivalence
              </div>
              <div
                data-testid="calc-revenue-equivalence"
                className="font-serif-brand font-bold leading-none text-[56px] sm:text-[72px]"
                style={{ color: EX_GREEN, letterSpacing: "-0.02em" }}
              >
                {fmt(revenueEquiv)}
              </div>
              <div
                className="mt-3 text-[13px] leading-relaxed max-w-md"
                style={{ color: SUBTEXT }}
              >
                The top-line revenue your business would otherwise need to win
                to deliver the same bottom-line impact.
              </div>
            </div>

            <div
              className="h-px w-full mb-8"
              style={{ background: RULE }}
              aria-hidden
            />

            {/* Secondary · Reinvestment Capital */}
            <div>
              <div
                className="text-[10px] font-mono tracking-[0.22em] uppercase mb-2 flex items-center gap-2"
                style={{ color: SUBTEXT }}
              >
                <CurrencyDollar weight="bold" size={12} />
                Reinvestment Capital · margin-equivalent
              </div>
              <div
                data-testid="calc-reinvestment-capital"
                className="font-mono font-bold text-[36px]"
                style={{ color: DEEP_SLATE }}
              >
                {fmt(reinvestment)}
              </div>
              <div
                className="mt-2 text-[12px]"
                style={{ color: SUBTEXT }}
              >
                Capital protected and released back to the business. Fund growth, product, or M&amp;A.
              </div>
            </div>

            <div className="mt-auto pt-8">
              <Link
                to="/command"
                data-testid="calc-cta-command"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-[12px] font-mono tracking-[0.16em] uppercase font-bold transition-transform hover:-translate-y-px"
                style={{
                  background: EX_GREEN,
                  color: ALABASTER,
                  border: `1px solid ${EX_GREEN}`,
                }}
              >
                Open the Command Center
                <ArrowRight weight="bold" size={13} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, min, max, step, onChange, raw, testid, float = false }) {
  return (
    <div className="mb-7" data-testid={`${testid}-field`}>
      <div className="flex items-baseline justify-between mb-2">
        <label
          className="text-[11px] font-mono tracking-[0.18em] uppercase"
          style={{ color: SUBTEXT }}
        >
          {label}
        </label>
        <span
          data-testid={`${testid}-value`}
          className="font-mono font-bold text-[16px]"
          style={{ color: DEEP_SLATE }}
        >
          {value}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={raw}
        onChange={(e) => onChange(float ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
        data-testid={`${testid}-slider`}
        className="w-full landing-range"
      />
    </div>
  );
}

/* ─── 6 · Footer ────────────────────────────────────────────────────────── */
function PulseTicker() {
  const signals = [
    { code: "LME COPPER",          delta: "-12.4%", dir: "DOWN" },
    { code: "BLS LABOR INDEX",     delta: "+2.1%",  dir: "UP"   },
    { code: "OFAC SANCTIONS",      delta: "100% CLEAN", dir: "OK"   },
    { code: "FRED · DCOILWTICO",   delta: "-1.4%",  dir: "DOWN" },
    { code: "GDELT · RED SEA",     delta: "DIVERSIONS HOLDING", dir: "WATCH" },
    { code: "USD / DXY",           delta: "+0.76",  dir: "UP"   },
    { code: "CBOE SKEW",           delta: "138.2",  dir: "WATCH" },
    { code: "SAM.gov FPRA",        delta: "12 PENDING", dir: "WATCH" },
    { code: "ECI Q-O-Q WAGES",     delta: "+0.9%",  dir: "UP"   },
    { code: "BAFT TRADE FINANCE",  delta: "INDEX 71.4", dir: "WATCH" },
    { code: "DAT VAN FREIGHT",     delta: "-3.2%",  dir: "DOWN" },
    { code: "OODA AUDIT READINESS", delta: "SIGNED-OFF", dir: "OK"   },
  ];
  // Duplicate so the marquee loops seamlessly
  const stream = [...signals, ...signals];

  const dirColor = (dir) => {
    if (dir === "OK") return EX_GREEN;
    // monochrome elsewhere — sober deep slate
    return DEEP_SLATE;
  };
  const dirGlyph = (dir) =>
    dir === "UP"    ? "▲"
    : dir === "DOWN"? "▼"
    : dir === "OK"  ? "●"
    :                 "·";

  return (
    <section
      data-testid="landing-pulse-ticker"
      aria-label="Live market pulse ticker"
      className="border-b overflow-hidden select-none"
      style={{
        background: DEEP_SLATE,
        borderColor: DEEP_SLATE,
        color: ALABASTER,
      }}
    >
      <div className="max-w-[1600px] mx-auto px-8 py-2.5 flex items-center gap-5">
        <div
          className="shrink-0 inline-flex items-center gap-2 pr-4 mr-1 border-r"
          style={{ borderColor: "rgba(249,250,251,0.15)" }}
        >
          <Pulse weight="fill" size={12} style={{ color: EX_GREEN }} />
          <span
            className="text-[10px] font-mono tracking-[0.22em] uppercase"
            style={{ color: ALABASTER }}
          >
            Live · Market Pulse
          </span>
        </div>

        <div
          className="flex-1 overflow-hidden relative"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0, #000 48px, #000 calc(100% - 48px), transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0, #000 48px, #000 calc(100% - 48px), transparent 100%)",
          }}
          data-testid="landing-pulse-ticker-rail"
        >
          <div className="pulse-ticker-track whitespace-nowrap">
            {stream.map((s, i) => (
              <span
                key={i}
                className="inline-flex items-baseline gap-2 px-6 font-mono text-[12px] tracking-[0.06em]"
                style={{ color: ALABASTER }}
                data-testid={i < signals.length ? `pulse-${s.code.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : undefined}
              >
                <span
                  className="text-[10px] tracking-[0.22em] uppercase"
                  style={{ color: "rgba(249,250,251,0.55)" }}
                >
                  {s.code}
                </span>
                <span className="font-semibold tabular-nums">{s.delta}</span>
                <span
                  className="text-[11px]"
                  style={{ color: dirColor(s.dir) === EX_GREEN ? EX_GREEN : "rgba(249,250,251,0.85)" }}
                >
                  {dirGlyph(s.dir)}
                </span>
                <span
                  className="text-[10px] mx-2"
                  style={{ color: "rgba(249,250,251,0.25)" }}
                >
                  ·
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Strategic Intelligence · PDI Defined ──────────────────────────────── */
function StrategicIntelligence() {
  // Three input feeds that fuse into the single PDI score.
  const FEEDS = [
    {
      tag: "01 · Live Spend",
      label: "Source-of-Truth Ledger",
      body: "Every PO, every award, every line item. Reconciled in real time.",
    },
    {
      tag: "02 · Market Indices",
      label: "FRED · BLS · GDELT · DAT",
      body: "Macro signal layered against your exposure. Commodity, FX, logistics.",
    },
    {
      tag: "03 · Supplier Signals",
      label: "10-K, OFAC, FPRA, UEI / CAGE",
      body: "Counter-party posture in plain English. Updated on every cycle.",
    },
  ];

  return (
    <section
      id="strategic-intelligence"
      data-testid="landing-strategic-intelligence"
      className="border-t"
      style={{ borderColor: RULE, background: ALABASTER }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Left column · copy */}
        <div className="lg:col-span-7">
          <div
            className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
            style={{ color: EX_GREEN }}
          >
            03 · Strategic Intelligence
          </div>
          <h2
            className="font-serif-brand font-semibold text-[36px] sm:text-[48px] leading-tight mb-5"
            style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
          >
            <span
              className="font-serif-brand font-extrabold"
              style={{ color: EX_GREEN }}
              data-testid="strategic-intelligence-pdi-word"
            >
              PDI
            </span>{" "}
            is the single score that grades category health.
          </h2>

          <p className="text-[15px] leading-relaxed mb-4" style={{ color: SUBTEXT }}>
            Procurement Decision Intelligence. Our proprietary metric. PDI fuses live spend data, market indices, and supplier signals into one board-grade score.
          </p>
          <p className="text-[15px] leading-relaxed mb-4" style={{ color: SUBTEXT }}>
            Inverted 100-point scale. Higher is better. Every category. Every supplier. Every award line.
          </p>
          <p className="text-[15px] leading-relaxed mb-7" style={{ color: SUBTEXT }}>
            PDI identifies the trapped capital sitting inside your portfolio. It surfaces the strategic proof required for boardroom-level decisions. One number. Auditable. Reconcilable. Defensible.
          </p>

          {/* Transparency callout · every PDI score drills back to source */}
          <div
            data-testid="strategic-intelligence-transparency"
            className="rounded-sm p-5 mb-7"
            style={{ background: "#FFFFFF", border: `1px solid ${RULE}`, borderLeft: `4px solid ${EX_GREEN}` }}
          >
            <div
              className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2 flex items-center gap-1.5"
              style={{ color: EX_GREEN }}
            >
              <ShieldCheck weight="fill" size={11} />
              Every score is fully auditable
            </div>
            <p className="text-[13.5px] leading-relaxed" style={{ color: DEEP_SLATE }}>
              Drill down on any{" "}
              <span className="font-extrabold" style={{ color: EX_GREEN }}>PDI</span>{" "}
              to see the raw market indices, contract lines, and supplier telemetry that generated it. No black box. No proprietary obfuscation. Every input is named. Every weight is shown. Every score reconciles to the source line.
            </p>
          </div>

          {/* Why it matters · three lock-in points */}
          {/* Cohort benchmarking · PDI vs anonymized industry medians */}
          <div
            data-testid="strategic-intelligence-cohort"
            className="rounded-sm p-5 mb-7"
            style={{ background: ALABASTER, border: `1px solid ${RULE}`, borderLeft: `4px solid ${DEEP_SLATE}` }}
          >
            <div
              className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2 flex items-center gap-1.5"
              style={{ color: EX_GREEN }}
            >
              <ChartLineUp weight="fill" size={11} />
              Cohort Benchmarking
            </div>
            <p className="text-[13.5px] leading-relaxed" style={{ color: DEEP_SLATE }}>
              Every <span className="font-extrabold" style={{ color: EX_GREEN }}>PDI</span> score is measured against anonymized industry medians for your sector. Your category health is not graded in isolation. The score tells you where you stand. The cohort tells you what is possible. The gap becomes the operating plan.
            </p>
          </div>

          <ul className="space-y-2.5 mb-8" data-testid="strategic-intelligence-impact">
            {[
              "Identifies trapped capital before it ages off the ledger.",
              "Provides the strategic proof a CFO can take into a board meeting.",
              "Standardizes the conversation across every category and business unit.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-[14px]" style={{ color: DEEP_SLATE }}>
                <CheckCircle weight="fill" size={14} style={{ color: EX_GREEN, marginTop: 4 }} />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <a
            href="#pdi-audit"
            data-testid="strategic-intelligence-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-[12px] font-mono tracking-[0.16em] uppercase font-bold transition-transform hover:-translate-y-px"
            style={{
              background: EX_GREEN,
              color: ALABASTER,
              border: `1px solid ${EX_GREEN}`,
            }}
          >
            Audit Your PDI Score
            <ArrowRight weight="bold" size={13} />
          </a>
        </div>

        {/* Right column · live-feed score card */}
        <aside
          className="lg:col-span-5"
          data-testid="strategic-intelligence-card"
        >
          <div
            className="p-8 rounded-sm"
            style={{ background: "#FFFFFF", border: `1px solid ${RULE}` }}
          >
            <div
              className="text-[10px] font-mono tracking-[0.22em] uppercase mb-2 flex items-center gap-2"
              style={{ color: EX_GREEN }}
            >
              <Sparkle weight="fill" size={11} />
              Sample Category · IT Services
            </div>
            <div
              data-testid="strategic-intelligence-score"
              className="font-serif-brand font-bold leading-none text-[120px] sm:text-[140px]"
              style={{ color: EX_GREEN, letterSpacing: "-0.04em" }}
            >
              87
            </div>
            <div
              className="text-[12px] font-mono tracking-[0.18em] uppercase mt-2 font-bold"
              style={{ color: SUBTEXT }}
            >
              Procurement Decision Intelligence
            </div>

            <div
              className="h-px w-full my-6"
              style={{ background: RULE }}
              aria-hidden
            />

            {/* Three fused inputs */}
            <div className="space-y-4">
              {FEEDS.map((f) => (
                <div
                  key={f.tag}
                  className="flex items-start gap-3"
                  data-testid={`strategic-intelligence-feed-${f.tag.split(" ")[0]}`}
                >
                  <span
                    className="inline-flex items-center justify-center h-7 w-7 rounded-sm shrink-0 font-mono text-[10px] font-bold"
                    style={{ background: DEEP_SLATE, color: ALABASTER }}
                  >
                    {f.tag.split(" ")[0]}
                  </span>
                  <div className="min-w-0">
                    <div
                      className="text-[10px] font-mono tracking-[0.20em] uppercase font-bold mb-0.5"
                      style={{ color: EX_GREEN }}
                    >
                      {f.tag.slice(f.tag.indexOf("·") + 2)}
                    </div>
                    <div
                      className="font-display text-[14px] font-bold tracking-tight"
                      style={{ color: DEEP_SLATE }}
                    >
                      {f.label}
                    </div>
                    <div className="text-[12px] leading-relaxed" style={{ color: SUBTEXT }}>
                      {f.body}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="mt-7 pt-4 text-[11px] leading-relaxed"
              style={{ color: SUBTEXT, borderTop: `1px solid ${RULE}` }}
            >
              PDI runs continuously. Every score is auditable to the source line.
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

/* ─── Intelligence Crew · Source Network ────────────────────────────────── */
const INTEL_SOURCES = [
  {
    pillar: "Market Indices",
    icon:   ChartLineUp,
    feeds: [
      { code: "FRED",  label: "Federal Reserve macroeconomic series." },
      { code: "BLS",   label: "Bureau of Labor Statistics. Producer Price Index and labor cost feeds." },
      { code: "LME",   label: "London Metal Exchange spot and forward curves." },
      { code: "ICE",   label: "Brent, WTI, and energy strip references." },
      { code: "BDI",   label: "Baltic Dry Index. Dry-bulk shipping freight rates." },
    ],
    impact: "Every commodity move is mapped against your exposed PO volume in real time.",
  },
  {
    pillar: "Supplier Telemetry",
    icon:   Robot,
    feeds: [
      { code: "10-K",   label: "SEC annual reports parsed for Item 1A risk language." },
      { code: "OFAC",   label: "Sanctions and denied-party lists checked nightly." },
      { code: "UEI",    label: "Unique Entity Identifier resolution across vendors." },
      { code: "CAGE",   label: "Commercial and Government Entity validation." },
      { code: "FPRA",   label: "Forward Pricing Rate Agreements for federal contractors." },
      { code: "D&B",    label: "Dun & Bradstreet financial stress score and PAYDEX." },
    ],
    impact: "Every counter-party arrives at the table with posture you can defend.",
  },
  {
    pillar: "Category Signals",
    icon:   Compass,
    feeds: [
      { code: "UNSPSC", label: "United Nations Standard Products and Services Code." },
      { code: "GDELT",  label: "Geopolitical event detection across 200+ languages." },
      { code: "DAT",    label: "DAT van and truckload freight rate benchmarks." },
      { code: "GSA·CALC", label: "GSA Contract Awarded Labor Category. Federal hourly rate medians." },
      { code: "BAFT",   label: "Bankers Association for Finance and Trade. Liquidity index." },
      { code: "ISM·PMI", label: "Institute for Supply Management. Sector-level industry telemetry." },
    ],
    impact: "Every category line is benchmarked against the market your suppliers operate in.",
  },
];

function IntelligenceCrew() {
  return (
    <section
      id="intelligence-crew"
      data-testid="landing-intelligence-crew"
      className="border-t"
      style={{ borderColor: RULE, background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div
              className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
              style={{ color: EX_GREEN }}
            >
              04 · Intelligence Crew
            </div>
            <h2
              className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
              style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
            >
              <span
                className="font-serif-brand font-extrabold"
                style={{ color: EX_GREEN }}
                data-testid="intel-crew-brand"
              >
                PROCUREO
              </span>{" "}
              runs a proprietary source network. Three pillars. One operating picture.
            </h2>
          </div>
          <p
            className="max-w-md text-[14px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            Diverse information sources do not help if they sit in silos.{" "}
            <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> fuses market indices, supplier telemetry, and category signals into a single ledger. The output is decision-grade by construction.
          </p>
        </div>

        {/* Three pillars · same shape, scannable */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-px"
          style={{ background: RULE }}
          data-testid="intel-crew-grid"
        >
          {INTEL_SOURCES.map((p, i) => (
            <PillarCard key={p.pillar} pillar={p} index={i + 1} />
          ))}
        </div>

        {/* How it works in tandem */}
        <div
          className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-px"
          style={{ background: RULE }}
          data-testid="intel-crew-orchestration"
        >
          <div className="lg:col-span-8 p-6" style={{ background: "#FFFFFF" }}>
            <div
              className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2"
              style={{ color: EX_GREEN }}
            >
              How the crew operates in tandem
            </div>
            <p className="text-[14px] leading-relaxed" style={{ color: DEEP_SLATE }}>
              Every signal feeds the same scoring engine. Market indices set the floor. Supplier telemetry sets the posture. Category signals set the benchmark. The three are reconciled inside the{" "}
              <span className="font-extrabold" style={{ color: EX_GREEN }}>PDI</span>{" "}
              fabric before any number leaves the building.
            </p>
            <p className="text-[14px] leading-relaxed mt-3" style={{ color: SUBTEXT }}>
              No source is allowed to fire in isolation. A copper rally without a supplier-exposure check is noise. A 10-K flag without a category-context overlay is alarmism. The crew is the guard against both.
            </p>
          </div>
          <div className="lg:col-span-4 p-6" style={{ background: ALABASTER }}>
            <div
              className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2"
              style={{ color: SUBTEXT }}
            >
              Refresh cadence
            </div>
            <ul className="space-y-2 text-[12.5px]" style={{ color: DEEP_SLATE }}>
              <li className="flex items-baseline justify-between gap-3 border-b pb-1.5"
                  style={{ borderColor: RULE }}>
                <span>Market indices</span>
                <span className="font-mono font-bold tabular-nums" style={{ color: EX_GREEN }}>5 min</span>
              </li>
              <li className="flex items-baseline justify-between gap-3 border-b pb-1.5"
                  style={{ borderColor: RULE }}>
                <span>Supplier telemetry</span>
                <span className="font-mono font-bold tabular-nums" style={{ color: EX_GREEN }}>nightly</span>
              </li>
              <li className="flex items-baseline justify-between gap-3 border-b pb-1.5"
                  style={{ borderColor: RULE }}>
                <span>Category signals</span>
                <span className="font-mono font-bold tabular-nums" style={{ color: EX_GREEN }}>hourly</span>
              </li>
              <li className="flex items-baseline justify-between gap-3 pt-1">
                <span className="font-bold">PDI reconciliation</span>
                <span className="font-mono font-bold tabular-nums" style={{ color: EX_GREEN }}>continuous</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function PillarCard({ pillar, index }) {
  const Icon = pillar.icon;
  const slug = pillar.pillar.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <article
      className="p-6 flex flex-col h-full"
      style={{ background: ALABASTER }}
      data-testid={`intel-pillar-${slug}`}
    >
      <div className="flex items-center justify-between mb-5">
        <span
          className="inline-flex items-center justify-center h-9 w-9 rounded-sm"
          style={{ background: DEEP_SLATE, color: ALABASTER }}
        >
          <Icon weight="bold" size={18} />
        </span>
        <span
          className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold"
          style={{ color: SUBTEXT }}
        >
          0{index} · Pillar
        </span>
      </div>
      <h3
        className="font-serif-brand font-semibold text-[22px] leading-tight mb-4"
        style={{ color: DEEP_SLATE }}
      >
        {pillar.pillar}
      </h3>
      <ul className="space-y-1.5 mb-5">
        {pillar.feeds.map((f) => (
          <li key={f.code} className="flex items-baseline gap-2.5">
            <span
              className="font-mono text-[10px] tracking-[0.14em] font-bold shrink-0"
              style={{
                color: EX_GREEN,
                minWidth: 64,
                paddingRight: 6,
                borderRight: `1px solid ${RULE}`,
              }}
            >
              {f.code}
            </span>
            <span className="text-[12px] leading-snug" style={{ color: DEEP_SLATE }}>
              {f.label}
            </span>
          </li>
        ))}
      </ul>
      <div
        className="mt-auto pt-3 text-[12px] leading-relaxed border-t"
        style={{ borderColor: RULE, color: SUBTEXT }}
      >
        {pillar.impact}
      </div>
    </article>
  );
}

/* ─── Margin Multiplier · Negotiation Supremacy ─────────────────────────── */
function NegotiationSupremacy() {
  // Sample line items the visitor would recognize from their own ledger.
  const LEVERS = [
    {
      line:     "Managed Services · Tier-2 helpdesk",
      market:   "$82 / hr",
      should:   "$68 / hr",
      delta:    "−17%",
      lever:    "GSA · CALC labor benchmark",
    },
    {
      line:     "Industrial Maintenance · pump assemblies",
      market:   "$4,210 / unit",
      should:   "$3,480 / unit",
      delta:    "−17.3%",
      lever:    "Should-cost decomposition · ISM PMI overlay",
    },
    {
      line:     "Logistics · van freight, Chicago lane",
      market:   "$2.41 / mile",
      should:   "$2.08 / mile",
      delta:    "−13.7%",
      lever:    "DAT van rate index · 30-day median",
    },
  ];

  return (
    <section
      id="negotiation-supremacy"
      data-testid="landing-negotiation-supremacy"
      className="border-t"
      style={{ borderColor: RULE, background: ALABASTER }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        {/* Left · copy */}
        <div className="lg:col-span-5">
          <div
            className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
            style={{ color: EX_GREEN }}
          >
            05 · Margin Multiplier · Negotiation Supremacy
          </div>
          <h2
            className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight mb-5"
            style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
          >
            Walk into every negotiation with a defensible position on letterhead.
          </h2>

          <p className="text-[15px] leading-relaxed mb-4" style={{ color: SUBTEXT }}>
            <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> delivers a Should-Cost model and a Rate Benchmark for every category line. Every supplier conversation starts from a defensible position. No guesswork.
          </p>
          <p className="text-[15px] leading-relaxed mb-7" style={{ color: SUBTEXT }}>
            Should-Cost decomposes the unit. Rate Benchmark grades the market. The two arrive in your hands before the meeting. The leverage is immediate. The leverage is auditable.
          </p>

          <ul className="space-y-2.5 mb-8" data-testid="negotiation-impact">
            {[
              "Every quote scored against a Should-Cost build before acceptance.",
              "Every rate benchmarked against GSA, DAT, BLS, and industry medians.",
              "Every category leader walks in with a defendable position. On letterhead.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-[14px]" style={{ color: DEEP_SLATE }}>
                <CheckCircle weight="fill" size={14} style={{ color: EX_GREEN, marginTop: 4 }} />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <a
            href="#pdi-audit"
            data-testid="negotiation-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-[12px] font-mono tracking-[0.16em] uppercase font-bold transition-transform hover:-translate-y-px"
            style={{ background: DEEP_SLATE, color: ALABASTER, border: `1px solid ${DEEP_SLATE}` }}
          >
            See It On Your Own Ledger
            <ArrowRight weight="bold" size={13} />
          </a>
        </div>

        {/* Right · sample leverage table */}
        <aside className="lg:col-span-7" data-testid="negotiation-table">
          <div
            className="rounded-sm overflow-hidden"
            style={{ background: "#FFFFFF", border: `1px solid ${RULE}` }}
          >
            <div
              className="px-5 py-3 flex items-center justify-between"
              style={{ background: "#F3F4F6", borderBottom: `1px solid ${RULE}` }}
            >
              <span className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold" style={{ color: SUBTEXT }}>
                Sample leverage · live category lines
              </span>
              <span className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold" style={{ color: EX_GREEN }}>
                Should-Cost · Rate Benchmark
              </span>
            </div>

            <table className="w-full text-left">
              <thead style={{ background: "#FAFAF7" }}>
                <tr className="text-[10px] font-mono tracking-[0.18em] uppercase" style={{ color: SUBTEXT }}>
                  <th className="px-5 py-3 font-bold">Line</th>
                  <th className="px-5 py-3 font-bold text-right">Market</th>
                  <th className="px-5 py-3 font-bold text-right">Should-Cost</th>
                  <th className="px-5 py-3 font-bold text-right">Leverage</th>
                </tr>
              </thead>
              <tbody>
                {LEVERS.map((row, i) => (
                  <tr
                    key={row.line}
                    className="border-t align-top"
                    style={{ borderColor: RULE }}
                    data-testid={`negotiation-row-${i}`}
                  >
                    <td className="px-5 py-4">
                      <div className="font-display font-bold text-[13px] tracking-tight" style={{ color: DEEP_SLATE }}>
                        {row.line}
                      </div>
                      <div className="text-[11px] font-mono mt-1" style={{ color: SUBTEXT }}>
                        {row.lever}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right font-mono text-[13px] tabular-nums" style={{ color: DEEP_SLATE }}>
                      {row.market}
                    </td>
                    <td className="px-5 py-4 text-right font-mono text-[13px] tabular-nums font-bold" style={{ color: DEEP_SLATE }}>
                      {row.should}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span
                        className="inline-flex items-center justify-center px-2 py-1 rounded-sm font-mono text-[12px] font-bold tabular-nums"
                        style={{
                          background: "#F0FDFA",
                          color: EX_GREEN,
                          border: `1px solid ${EX_GREEN}33`,
                        }}
                      >
                        {row.delta}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div
              className="px-5 py-3 text-[11px] font-mono"
              style={{ background: "#FAFAF7", borderTop: `1px solid ${RULE}`, color: SUBTEXT }}
            >
              Every row is the output of a <span className="font-bold" style={{ color: EX_GREEN }}>PDI</span> lever. Reconciled to the source-of-truth ledger. Defensible at every level.
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

/* ─── Margin Multiplier · Working Capital Release ───────────────────────── */
function WorkingCapitalRelease() {
  // 90-day phased release schedule. Real-feeling, not fluffy.
  const PHASES = [
    {
      window: "Days 0 – 30",
      title:  "Surface",
      body:   "Stale contracts identified. Auto-renewals flagged. Off-contract spend re-classified.",
      kpi:    "$8.4M",
      label:  "trapped capital identified",
    },
    {
      window: "Days 31 – 60",
      title:  "Reclaim",
      body:   "Contractual Rate Realignment runs against the live benchmark. SKU Rationalization consolidates the long tail. Volume rebates triggered. Inactive PO commitments released.",
      kpi:    "$5.2M",
      label:  "reclaimed to the balance sheet",
    },
    {
      window: "Days 61 – 90",
      title:  "Redeploy",
      body:   "Capital routed into the growth budget. Reinvested in product, M&A, or shareholder return.",
      kpi:    "$3.2M",
      label:  "redeployed to growth",
    },
  ];

  return (
    <section
      id="working-capital-release"
      data-testid="landing-working-capital"
      className="border-t"
      style={{ borderColor: RULE, background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div
              className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
              style={{ color: EX_GREEN }}
            >
              07 · Margin Multiplier · Working Capital Release
            </div>
            <h2
              className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
              style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
            >
              Cash trapped in stale contracts. Moved to the growth budget in 90 days.
            </h2>
          </div>
          <p
            className="max-w-md text-[14px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> hunts the working capital your portfolio is sitting on. Auto-renewals. Off-contract spend. Dormant POs. We find it. We free it. We route it where it pays.
          </p>
        </div>

        {/* 90-day phased release timeline */}
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-px"
          style={{ background: RULE }}
          data-testid="working-capital-phases"
        >
          {PHASES.map((p, i) => (
            <article
              key={p.title}
              className="p-6 flex flex-col h-full"
              style={{ background: ALABASTER }}
              data-testid={`wcr-phase-${p.title.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-5">
                <span
                  className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold"
                  style={{ color: SUBTEXT }}
                >
                  0{i + 1} · {p.window}
                </span>
                <span
                  className="inline-flex items-center justify-center h-9 w-9 rounded-sm"
                  style={{ background: DEEP_SLATE, color: ALABASTER }}
                >
                  {i === 0 ? <Eye weight="bold" size={18} /> :
                   i === 1 ? <Scales weight="bold" size={18} /> :
                             <ChartLineUp weight="bold" size={18} />}
                </span>
              </div>
              <h3
                className="font-serif-brand font-semibold text-[24px] leading-tight mb-3"
                style={{ color: DEEP_SLATE }}
              >
                {p.title}
              </h3>
              <p className="text-[13px] leading-relaxed mb-5" style={{ color: SUBTEXT }}>
                {p.body}
              </p>
              <div className="mt-auto pt-4 border-t" style={{ borderColor: RULE }}>
                <div
                  className="font-display font-extrabold text-[36px] tabular-nums leading-none"
                  style={{ color: EX_GREEN, letterSpacing: "-0.03em" }}
                >
                  {p.kpi}
                </div>
                <div
                  className="text-[10.5px] font-mono tracking-[0.18em] uppercase font-bold mt-1.5"
                  style={{ color: SUBTEXT }}
                >
                  {p.label}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Named mechanisms · the two specific operational levers */}
        <div
          className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-px"
          style={{ background: RULE }}
          data-testid="working-capital-mechanisms"
        >
          {[
            {
              code:  "Mechanism 01",
              title: "Contractual Rate Realignment",
              body:  "Every active rate card is regraded against the live Should-Cost build and Rate Benchmark. Variance above tolerance triggers a renegotiation packet. Letter of authority on letterhead.",
            },
            {
              code:  "Mechanism 02",
              title: "SKU Rationalization",
              body:  "The long-tail catalog is decomposed. Duplicate SKUs collapsed. Low-velocity items retired. Aggregate volume redirected to negotiated agreements.",
            },
          ].map((m) => (
            <div
              key={m.title}
              className="p-5"
              style={{ background: "#FFFFFF" }}
              data-testid={`wcr-mechanism-${m.code.split(" ")[1]}`}
            >
              <div
                className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-1"
                style={{ color: EX_GREEN }}
              >
                {m.code}
              </div>
              <div
                className="font-serif-brand font-semibold text-[18px] leading-tight mb-2"
                style={{ color: DEEP_SLATE }}
              >
                {m.title}
              </div>
              <p className="text-[13px] leading-relaxed" style={{ color: SUBTEXT }}>
                {m.body}
              </p>
            </div>
          ))}
        </div>

        {/* Roll-up · margin multiplier bottom strip */}
        <div
          className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-px"
          style={{ background: RULE }}
          data-testid="working-capital-rollup"
        >
          <div className="lg:col-span-8 p-6" style={{ background: ALABASTER }}>
            <div
              className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-2"
              style={{ color: EX_GREEN }}
            >
              The Margin Multiplier
            </div>
            <p className="text-[14px] leading-relaxed" style={{ color: DEEP_SLATE }}>
              Capital released at the procurement layer compounds upstream. Each dollar reclaimed lands in the growth budget at full margin equivalence. The board does not see a cost cut. The board sees revenue equivalence.
            </p>
            <p className="text-[14px] leading-relaxed mt-3" style={{ color: SUBTEXT }}>
              <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> measures the release. <span className="font-extrabold" style={{ color: EX_GREEN }}>PDI</span> defends the release. The CFO carries the release into the next board meeting.
            </p>
          </div>
          <div className="lg:col-span-4 p-6" style={{ background: "#FFFFFF" }}>
            <div
              className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-3"
              style={{ color: SUBTEXT }}
            >
              90-Day Pipeline · sample mid-market portfolio
            </div>
            <div
              data-testid="working-capital-pipeline"
              className="flex items-baseline justify-between gap-2 font-mono tabular-nums"
            >
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.18em] uppercase" style={{ color: SUBTEXT }}>Identified</span>
                <span className="font-display font-extrabold text-[22px] leading-none mt-1" style={{ color: DEEP_SLATE }}>$8.4M</span>
              </div>
              <ArrowRight weight="bold" size={14} style={{ color: SUBTEXT, marginTop: 12 }} />
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.18em] uppercase" style={{ color: SUBTEXT }}>Reclaimed</span>
                <span className="font-display font-extrabold text-[22px] leading-none mt-1" style={{ color: DEEP_SLATE }}>$5.2M</span>
              </div>
              <ArrowRight weight="bold" size={14} style={{ color: SUBTEXT, marginTop: 12 }} />
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.18em] uppercase" style={{ color: EX_GREEN }}>Redeployed</span>
                <span className="font-display font-extrabold text-[22px] leading-none mt-1" style={{ color: EX_GREEN }}>$3.2M</span>
              </div>
            </div>
            <div
              className="text-[11px] font-mono mt-4 leading-relaxed"
              style={{ color: SUBTEXT }}
            >
              Same dollars moving through stages. Indexed to live PDI exposure.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Sign-Off Gate · Human-in-the-Loop Accountability ───────────────── */
function SignOffGate() {
  // Three-step loop. The Intelligence Crew proposes. The human signs off. Then the action commits.
  const STEPS = [
    {
      n:     "01",
      title: "Crew Proposes",
      icon:  Robot,
      body:  "The autonomous Intelligence Crew composes a strategy, builds the evidence packet, and stages the recommendation. Nothing is executed at this stage.",
    },
    {
      n:     "02",
      title: "Lead Signs Off",
      icon:  ShieldCheck,
      body:  "A named human lead reviews the strategy and the underlying PDI drill-down. The lead can refine, reject, or sign off. The sign-off is named and timestamped.",
    },
    {
      n:     "03",
      title: "Commit and Execute",
      icon:  Lightning,
      body:  "Only a signed-off strategy reaches execution. The award, the renegotiation packet, the SKU rationalization. All actions trace back to the lead who signed.",
    },
  ];

  return (
    <section
      id="signoff-gate"
      data-testid="landing-signoff-gate"
      className="border-t"
      style={{ borderColor: RULE, background: ALABASTER }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div
              className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
              style={{ color: EX_GREEN }}
            >
              08 · Sign-Off Gate
            </div>
            <h2
              className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
              style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
            >
              The autonomous crew proposes. A human lead signs off. Then the system commits.
            </h2>
          </div>
          <p
            className="max-w-md text-[14px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> is autonomous up to the gate. Past the gate, a named operative carries the decision. Every action is signed. Every action is recoverable. The buck has a name on it.
          </p>
        </div>

        {/* Three-step gate */}
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-px"
          style={{ background: RULE }}
          data-testid="signoff-steps"
        >
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.n}
                className="p-6 flex flex-col h-full"
                style={{ background: "#FFFFFF" }}
                data-testid={`signoff-step-${s.n}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span
                    className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold"
                    style={{ color: SUBTEXT }}
                  >
                    {s.n} · Gate
                  </span>
                  <span
                    className="inline-flex items-center justify-center h-9 w-9 rounded-sm"
                    style={{ background: DEEP_SLATE, color: ALABASTER }}
                  >
                    <Icon weight="bold" size={18} />
                  </span>
                </div>
                <h3
                  className="font-serif-brand font-semibold text-[22px] leading-tight mb-3"
                  style={{ color: DEEP_SLATE }}
                >
                  {s.title}
                </h3>
                <p className="text-[13px] leading-relaxed" style={{ color: SUBTEXT }}>
                  {s.body}
                </p>
              </article>
            );
          })}
        </div>

        {/* Accountability anchor strip */}
        <div
          className="mt-8 rounded-sm p-5 flex items-start gap-4"
          style={{ background: "#FFFFFF", border: `1px solid ${RULE}`, borderLeft: `4px solid ${EX_GREEN}` }}
          data-testid="signoff-anchor"
        >
          <ShieldCheck weight="fill" size={22} style={{ color: EX_GREEN, marginTop: 2 }} />
          <div>
            <div
              className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-1.5"
              style={{ color: EX_GREEN }}
            >
              Sign Off and Commit
            </div>
            <p className="text-[14px] leading-relaxed" style={{ color: DEEP_SLATE }}>
              No action leaves <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> without a signed-off decision from a human lead. The autonomous Intelligence Crew is a force-multiplier. It is not a final authority. The named operative is the final authority. Every audit trail closes on a person.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Strategic Overlay · Non-Disruptive Integration ────────────────────── */
function StrategicOverlay() {
  const PROMISES = [
    {
      icon: Lightning,
      kpi:  "Hours",
      label: "Integration",
      body:  "Read-only connectors against SAP, Oracle, Coupa, NetSuite, and the spreadsheets you already export. No schema migrations. No data warehouse rebuild.",
    },
    {
      icon: Pulse,
      kpi:  "Seconds",
      label: "Insight",
      body:  "PDI scores stream the moment data lands. The operating picture updates continuously. Sub-second drill-down on any line.",
    },
    {
      icon: ShieldCheck,
      kpi:  "Zero",
      label: "Disruption",
      body:  "PROCUREO is a non-disruptive intelligence layer. Your ERP stays the system of record. Your finance team keeps their workflow. Your IT team does not lose a weekend.",
    },
  ];

  return (
    <section
      id="strategic-overlay"
      data-testid="landing-strategic-overlay"
      className="border-t"
      style={{ borderColor: RULE, background: ALABASTER }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div
              className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
              style={{ color: EX_GREEN }}
            >
              02 · Strategic Overlay
            </div>
            <h2
              className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
              style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
            >
              <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span>{" "}
              sits above your stack. It does not replace it.
            </h2>
          </div>
          <p
            className="max-w-md text-[14px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            The cleanest enterprise rollout is the one nobody notices.{" "}
            <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> reads. It scores. It surfaces. It signs nothing in your ERP without a named sign-off.
          </p>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-px"
          style={{ background: RULE }}
          data-testid="strategic-overlay-grid"
        >
          {PROMISES.map((p, i) => {
            const Icon = p.icon;
            return (
              <article
                key={p.label}
                className="p-6 flex flex-col h-full"
                style={{ background: "#FFFFFF" }}
                data-testid={`overlay-card-${p.label.toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span
                    className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold"
                    style={{ color: SUBTEXT }}
                  >
                    0{i + 1} · Overlay
                  </span>
                  <span
                    className="inline-flex items-center justify-center h-9 w-9 rounded-sm"
                    style={{ background: DEEP_SLATE, color: ALABASTER }}
                  >
                    <Icon weight="bold" size={18} />
                  </span>
                </div>
                <div
                  className="font-display font-extrabold leading-none text-[44px] tabular-nums tracking-tighter"
                  style={{ color: EX_GREEN, letterSpacing: "-0.02em" }}
                >
                  {p.kpi}
                </div>
                <div
                  className="text-[10.5px] font-mono tracking-[0.22em] uppercase font-bold mt-1.5 mb-3"
                  style={{ color: EX_GREEN }}
                >
                  {p.label}
                </div>
                <p className="text-[13px] leading-relaxed" style={{ color: SUBTEXT }}>
                  {p.body}
                </p>
              </article>
            );
          })}
        </div>

        <div
          className="mt-8 rounded-sm p-5 flex items-start gap-4"
          style={{ background: "#FFFFFF", border: `1px solid ${RULE}`, borderLeft: `4px solid ${EX_GREEN}` }}
          data-testid="strategic-overlay-promise"
        >
          <Pulse weight="fill" size={20} style={{ color: EX_GREEN, marginTop: 3 }} />
          <p className="text-[14.5px] leading-relaxed font-medium" style={{ color: DEEP_SLATE }}>
            Spreadsheet exports live the same day. ERP read-connectors live in weeks. Zero disruption to your existing system of record.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Margin Multiplier · Counterparty Resilience ───────────────────────── */
function CounterpartyResilience() {
  const SHOCKS = [
    {
      code:  "01 · Single-Source Shock",
      title: "Concentration Audit",
      body:  "Every supplier graded on share-of-spend. Threshold breaches flagged in advance. Dual-source candidates pre-qualified.",
      anchor:"Threshold · 70% share-of-spend",
    },
    {
      code:  "02 · Entity Risk",
      title: "Posture Telemetry",
      body:  "10-K Item 1A language, OFAC matches, D&B financial stress score, and trade-finance liquidity. Every signal feeds the counter-party PDI.",
      anchor:"Sources · 10-K · OFAC · D&B · BAFT",
    },
    {
      code:  "03 · Geopolitical Exposure",
      title: "Lane and Country Map",
      body:  "GDELT event detection layered on your shipping lanes. Red Sea reroutes, port shutdowns, sanctions cascades. Surfaced before the press release.",
      anchor:"Window · 24-hour rolling",
    },
  ];

  return (
    <section
      id="counterparty-resilience"
      data-testid="landing-counterparty-resilience"
      className="border-t"
      style={{ borderColor: RULE, background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div
              className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
              style={{ color: EX_GREEN }}
            >
              06 · Margin Multiplier · Counterparty Resilience
            </div>
            <h2
              className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
              style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
            >
              The best rate is the one your supplier can actually deliver.
            </h2>
          </div>
          <p
            className="max-w-md text-[14px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> audits for Single-Source Shock and Entity Risk alongside price efficiency. We do not just find the best rate. We find the most resilient one. Continuity is treated as a co-equal lever to cost.
          </p>
        </div>

        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-px"
          style={{ background: RULE }}
          data-testid="resilience-grid"
        >
          {SHOCKS.map((s) => (
            <article
              key={s.code}
              className="p-6 flex flex-col h-full"
              style={{ background: ALABASTER }}
              data-testid={`resilience-card-${s.code.split(" ")[0]}`}
            >
              <div
                className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-3"
                style={{ color: EX_GREEN }}
              >
                {s.code}
              </div>
              <h3
                className="font-serif-brand font-semibold text-[22px] leading-tight mb-3"
                style={{ color: DEEP_SLATE }}
              >
                {s.title}
              </h3>
              <p className="text-[13px] leading-relaxed mb-4" style={{ color: SUBTEXT }}>
                {s.body}
              </p>
              <div
                className="mt-auto pt-3 text-[11px] font-mono tracking-[0.14em] uppercase font-bold border-t"
                style={{ borderColor: RULE, color: DEEP_SLATE }}
              >
                {s.anchor}
              </div>
            </article>
          ))}
        </div>

        <div
          className="mt-8 rounded-sm p-5 flex items-start gap-4"
          style={{ background: ALABASTER, border: `1px solid ${RULE}`, borderLeft: `4px solid ${EX_GREEN}` }}
          data-testid="resilience-pull"
        >
          <ShieldCheck weight="fill" size={20} style={{ color: EX_GREEN, marginTop: 3 }} />
          <p className="text-[14px] leading-relaxed font-medium" style={{ color: DEEP_SLATE }}>
            Price without resilience is a number that does not survive contact with the market. <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> grades both. The lowest defensible rate from the most resilient supplier wins.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Personnel Board · System of Systems ───────────────────────────────── */
const PERSONNEL = [
  { name: "Strategic Synthesizer",        tier: "Orchestrator", icon: Brain,
    pursuit: "Composing the Q1 board verdict across 12 active categories." },
  { name: "Decision-Adjusted Auditor",     tier: "Orchestrator", icon: ShieldCheck,
    pursuit: "Locking decision-adjusted sign-offs for 180 award rows." },
  { name: "Action Recommender",            tier: "Orchestrator", icon: Crosshair,
    pursuit: "Drafting 30-day plays for 14 high-leverage suppliers." },
  { name: "PDI Sentinel",                  tier: "Technical Agent", icon: ShieldCheck,
    pursuit: "Re-scoring 8 counterparties after the latest 10-K cycle." },
  { name: "Commodity Observer",            tier: "Technical Agent", icon: ChartBar,
    pursuit: "Tracking copper +3.2% MoM against 24 referenced POs." },
  { name: "Filing Extraction Agent",       tier: "Technical Agent", icon: FileCsv,
    pursuit: "Parsing Item 1A risk language across this week's SEC filings." },
  { name: "Should-Cost Analyst",           tier: "Technical Agent", icon: ChartBar,
    pursuit: "Decomposing 6 IT-services awards into FAR / DCAA cost buckets." },
  { name: "Rate Benchmark Analyst",        tier: "Technical Agent", icon: TrendUp,
    pursuit: "Benchmarking professional-services unit rates vs. GSA · CALC." },
  { name: "Supplier Concentration Analyst", tier: "Technical Agent", icon: Users,
    pursuit: "Flagging 3 single-source exposures above 70% spend share." },
  { name: "UNSPSC Mapper",                 tier: "Technical Agent", icon: Compass,
    pursuit: "Classifying the new Q1 spend file into segments & families." },
  { name: "Entity Resolution Agent",       tier: "Technical Agent", icon: Robot,
    pursuit: "Resolving 412 vendor records to UEI / CAGE / DUNS." },
  { name: "Integrity Assertion Runner",    tier: "System", icon: ShieldCheck,
    pursuit: "Running the 11 SQL assertions powering the CFO badge live." },
];

function PersonnelBoard() {
  return (
    <section
      id="personnel-board"
      data-testid="landing-personnel-board"
      className="border-t"
      style={{ borderColor: RULE, background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div
              className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
              style={{ color: EX_GREEN }}
            >
              09 · The Agents Live
            </div>
            <h2
              className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
              style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
            >
              Twelve agents. One operating picture.
            </h2>
          </div>
          <p
            className="max-w-md text-[14px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            Every agent carries a Pulse Score graded against the margin it actually moved. <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> audits agent performance against attributable outcomes. The score names the agents driving results. The same score names the gaps. Agent operations become a measured discipline, not a black box.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px"
             style={{ background: RULE }}
             data-testid="landing-personnel-grid">
          {PERSONNEL.map((p) => (
            <PersonnelCard key={p.name} agent={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PersonnelCard({ agent }) {
  const Icon = agent.icon || Robot;
  const slug = agent.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <article
      data-testid={`personnel-card-${slug}`}
      className="p-6 transition-colors hover:bg-[#FAFAFA]"
      style={{ background: ALABASTER }}
    >
      <div className="flex items-start gap-4">
        <span
          className="inline-flex items-center justify-center h-10 w-10 rounded-sm shrink-0"
          style={{ background: DEEP_SLATE, color: ALABASTER }}
        >
          <Icon weight="bold" size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="text-[10px] font-mono tracking-[0.22em] uppercase mb-1.5"
            style={{ color: SUBTEXT }}
          >
            {agent.tier}
          </div>
          <h3
            className="font-serif-brand font-semibold text-[18px] leading-tight mb-2"
            style={{ color: DEEP_SLATE }}
          >
            {agent.name}
          </h3>
          <div
            className="text-[10px] font-mono tracking-[0.18em] uppercase mb-1"
            style={{ color: EX_GREEN }}
          >
            Current Pursuit
          </div>
          <p className="text-[13px] leading-relaxed" style={{ color: SUBTEXT }}>
            {agent.pursuit}
          </p>
        </div>
      </div>
    </article>
  );
}

/* ─── Audit Hook · Drag-drop sample CSV → mini PDI Report ───────────────── */
function AuditHook() {
  const [stage, setStage] = useState("IDLE");          // IDLE | DRAG | LOADING | DONE
  const [report, setReport] = useState(null);
  const [steps, setSteps] = useState([]);
  const [fileName, setFileName] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    setStage("LOADING");
    setSteps([]);
    setReport(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result || "");
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      const rows  = Math.max(1, Math.min(10, lines.length - 1));
      runPipeline(rows);
    };
    reader.onerror = () => runPipeline(10);   // fallback — assume 10 rows
    reader.readAsText(file);
  };

  const runPipeline = (rows) => {
    const seq = [
      { label: "Technical Agent · UNSPSC mapping",        delay: 700 },
      { label: "Technical Agent · entity resolution",      delay: 1100 },
      { label: "Terminal Sentinel · counter-party check",  delay: 1600 },
      { label: "Decision-Adjusted Auditor · PDI scoring",  delay: 2100 },
    ];
    seq.forEach((s) => {
      timerRef.current = setTimeout(() => {
        setSteps((prev) => [...prev, s.label]);
      }, s.delay);
    });

    // Final report — synthesized from row count + a deterministic finding bucket
    timerRef.current = setTimeout(() => {
      // Score: high if rows clean, dip slightly for larger samples to surface a finding
      const baseScore = 78 + ((rows * 3) % 14);   // 78–91
      const score     = Math.max(62, Math.min(94, baseScore));
      const tier      = score >= 85 ? "GREEN" : score >= 75 ? "YELLOW" : "AMBER";
      const findings = [
        "1 supplier shows >70% spend concentration. Single-source exposure flagged.",
        "2 UNSPSC codes ambiguous. Probable mis-classification in Marketing services.",
        "1 counter-party missing UEI or CAGE. Entity resolution incomplete.",
        "3 award lines exceed segment median rate by >18%. Should-cost review recommended.",
      ];
      const finding = findings[rows % findings.length];

      setReport({ score, tier, rows, finding });
      setStage("DONE");
    }, 2600);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setStage((prev) => (prev === "LOADING" ? prev : "IDLE"));
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };
  const onDragOver  = (e) => { e.preventDefault(); if (stage !== "LOADING") setStage("DRAG"); };
  const onDragLeave = ()  => { if (stage === "DRAG") setStage("IDLE"); };

  const reset = () => {
    setStage("IDLE");
    setReport(null);
    setSteps([]);
    setFileName(null);
  };

  // Generate a sample CSV blob the visitor can drop right back in
  const sampleCsvHref = useMemo(() => {
    const csv = [
      "po_number,supplier,unspsc,baseline,realized",
      "PO-A001,Acme Manufacturing LLC,24101500,420000,398000",
      "PO-A002,Globex Logistics Inc,78101600,180000,164500",
      "PO-A003,Initech Consulting,80101500,260000,240000",
      "PO-A004,Stark Components Co,32101500,540000,512000",
      "PO-A005,Hooli Cloud Services,43232300,720000,684000",
      "PO-A006,Wayne Industrial Supply,24112800,310000,302000",
      "PO-A007,Massive Dynamic,41111900,485000,440000",
      "PO-A008,Cyberdyne Sub-systems,32151700,255000,238000",
      "PO-A009,Soylent Materials,50100000,99000,93500",
      "PO-A010,Tyrell Systems Lab,43211500,612000,588000",
    ].join("\n");
    return "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
  }, []);

  const tierColor = report?.tier === "GREEN" ? EX_GREEN : DEEP_SLATE;

  return (
    <section
      id="pdi-audit"
      data-testid="landing-audit-hook"
      className="border-t"
      style={{ borderColor: RULE, background: ALABASTER }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left · copy */}
        <div className="lg:col-span-5">
          <div
            className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
            style={{ color: EX_GREEN }}
          >
            10 · PDI Audit Hook
          </div>
          <h2
            className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight mb-5"
            style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
          >
            Request an immediate PDI audit.
          </h2>
          <p
            className="text-[14px] leading-relaxed mb-6"
            style={{ color: SUBTEXT }}
          >
            Drop a 10-row sample of your spend. A Technical Agent maps the UNSPSC segments. The Terminal Sentinel checks counter-party posture. The Auditor returns a Procurement Decision Intelligence score and the single most material finding. Most audits finish in under three seconds. Nothing leaves your browser.
          </p>

          <a
            href={sampleCsvHref}
            download="procureo-sample-spend.csv"
            data-testid="audit-download-sample"
            className="inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.16em] uppercase font-bold"
            style={{ color: DEEP_SLATE, textDecoration: "underline" }}
          >
            <FileCsv weight="bold" size={13} />
            Download 10-row sample CSV
          </a>
        </div>

        {/* Right · drop zone + pipeline + report */}
        <div className="lg:col-span-7">
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => stage !== "LOADING" && inputRef.current?.click()}
            role="button"
            tabIndex={0}
            data-testid="audit-dropzone"
            className="rounded-sm p-8 transition-colors cursor-pointer"
            style={{
              background: "#FFFFFF",
              border: `2px dashed ${stage === "DRAG" ? EX_GREEN : "#CBD5E1"}`,
              outline: stage === "DRAG" ? `2px solid ${EX_GREEN}33` : "none",
            }}
          >
            <input
              type="file"
              ref={inputRef}
              accept=".csv,text/csv,text/plain"
              data-testid="audit-file-input"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />

            {stage === "IDLE" || stage === "DRAG" ? (
              <div className="flex flex-col items-center text-center py-6">
                <UploadSimple weight="bold" size={28} style={{ color: DEEP_SLATE }} />
                <div
                  className="mt-4 font-serif-brand font-semibold text-[22px]"
                  style={{ color: DEEP_SLATE }}
                >
                  Drop a 10-row CSV here
                </div>
                <div
                  className="mt-2 text-[12px] font-mono tracking-[0.14em] uppercase"
                  style={{ color: SUBTEXT }}
                >
                  …or click to browse · processed locally
                </div>
              </div>
            ) : null}

            {stage === "LOADING" ? (
              <div className="py-2" data-testid="audit-pipeline">
                <div
                  className="text-[10px] font-mono tracking-[0.22em] uppercase mb-4 flex items-center gap-2"
                  style={{ color: EX_GREEN }}
                >
                  <Pulse weight="fill" size={11} /> Auditing {fileName || "your sample"}…
                </div>
                <ol className="space-y-2.5">
                  {[
                    "Technical Agent · UNSPSC mapping",
                    "Technical Agent · entity resolution",
                    "Terminal Sentinel · counter-party check",
                    "Decision-Adjusted Auditor · PDI scoring",
                  ].map((s) => {
                    const done = steps.includes(s);
                    return (
                      <li
                        key={s}
                        data-testid={`audit-step-${s.split(" ")[0].toLowerCase()}`}
                        className="flex items-center gap-3 text-[13px]"
                        style={{ color: done ? DEEP_SLATE : SUBTEXT }}
                      >
                        <span
                          className="inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-mono font-bold"
                          style={{
                            background: done ? EX_GREEN : "transparent",
                            color: done ? ALABASTER : SUBTEXT,
                            border: `1px solid ${done ? EX_GREEN : "#CBD5E1"}`,
                          }}
                        >
                          {done ? "✓" : "·"}
                        </span>
                        <span className={done ? "font-semibold" : ""}>{s}</span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ) : null}

            {stage === "DONE" && report ? (
              <div data-testid="audit-report" className="grid grid-cols-1 sm:grid-cols-12 gap-6 items-center">
                <div className="sm:col-span-5">
                  <div
                    className="text-[10px] font-mono tracking-[0.22em] uppercase mb-1.5"
                    style={{ color: EX_GREEN }}
                  >
                    PDI · {report.tier}
                  </div>
                  <div
                    data-testid="audit-report-score"
                    className="font-serif-brand font-bold leading-none text-[80px]"
                    style={{ color: tierColor, letterSpacing: "-0.03em" }}
                  >
                    {report.score}
                  </div>
                  <div
                    className="text-[11px] font-mono tracking-[0.18em] uppercase mt-2"
                    style={{ color: SUBTEXT }}
                  >
                    Procurement Decision Intelligence
                  </div>
                </div>
                <div className="sm:col-span-7">
                  <div
                    className="text-[10px] font-mono tracking-[0.22em] uppercase mb-2"
                    style={{ color: SUBTEXT }}
                  >
                    Top Finding
                  </div>
                  <p
                    data-testid="audit-report-finding"
                    className="text-[14.5px] leading-relaxed font-medium"
                    style={{ color: DEEP_SLATE }}
                  >
                    {report.finding}
                  </p>
                  <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <Link
                      to="/command"
                      data-testid="audit-cta-open"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[11px] font-mono tracking-[0.16em] uppercase font-bold"
                      style={{ background: DEEP_SLATE, color: ALABASTER, border: `1px solid ${DEEP_SLATE}` }}
                    >
                      Open the Full Audit
                      <ArrowRight weight="bold" size={12} />
                    </Link>
                    <button
                      type="button"
                      onClick={reset}
                      data-testid="audit-reset"
                      className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.18em] uppercase font-bold"
                      style={{ color: SUBTEXT }}
                    >
                      <X weight="bold" size={11} /> Drop another file
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div
            className="mt-4 text-[11px] font-mono tracking-[0.14em] uppercase"
            style={{ color: SUBTEXT }}
          >
            Files are parsed in-browser. No upload leaves this device.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Ascension · Mid-market → Fortune 500 → Fortune 50 ─────────────────── */
const ASCENSION = [
  {
    rank: "01",
    tier: "Mid-Market",
    range: "$50M – $500M strategic spend",
    icon: Briefcase,
    headline: "Plug-in operating picture in 30 days.",
    body:
      "Bring one category live. Validate decision-adjusted reinvestment capital. Then expand. Built for finance leaders on lean teams. Procureo covers the analyst bench you do not have.",
    proof: ["1 category live · week 2", "11 / 11 CFO integrity checks", "First reinvestment capital · week 5"],
    price: "Pilot engagement · custom",
  },
  {
    rank: "02",
    tier: "Fortune 500",
    range: "$500M – $5B strategic spend",
    icon: Buildings,
    headline: "Cross-category offense at enterprise scale.",
    body:
      "Every business unit on one partnership. Every finding reconciled against the source-of-truth ledger before it leaves the building. Procurement becomes a measured offensive function, not the cost center your CFO tolerates.",
    proof: ["Multi-BU rollout · 60 days", "$48M+ signed-off reinvestment", "Audit-grade integrity · 11/11 verified"],
    price: "Annual contract · custom",
  },
  {
    rank: "03",
    tier: "Fortune 50",
    range: "$5B+ strategic spend",
    icon: ChartLineUp,
    headline: "Boardroom command at scale.",
    body:
      "100K-row SAP imports. Greater than 85% UNSPSC classification accuracy. Procureo embedded across every category, on call 24/7. The durable competitive position your competitors cannot copy without a year of accumulated calibration.",
    proof: ["100K-row stress validated", "Revenue Equivalence > $1B", "Global · multi-region resilience"],
    price: "Annual contract · custom",
  },
];

function Ascension() {
  return (
    <section
      id="ascension"
      data-testid="landing-ascension"
      className="border-t"
      style={{ borderColor: RULE, background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div
              className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
              style={{ color: EX_GREEN }}
            >
              12 · The Ascension
            </div>
            <h2
              className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
              style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
            >
              From mid-market to the Fortune 50. One operating picture.
            </h2>
          </div>
          <p
            className="max-w-md text-[14px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            The same Procureo partnership scales with you. First as the analyst bench a lean team does not have. Then as the embedded operating layer running every category for a global enterprise — calibrated, audit-grade, and improving every quarter.
          </p>
        </div>

        {/* Three-step ramp */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px" style={{ background: RULE }}
             data-testid="landing-ascension-ramp">
          {ASCENSION.map((step, i) => (
            <AscensionCard key={step.tier} step={step} index={i} />
          ))}
        </div>

        {/* Closing arrow line + CTA */}
        <div className="mt-10 flex items-center justify-between flex-wrap gap-6">
          <div
            className="flex items-center gap-3 text-[11px] font-mono tracking-[0.22em] uppercase"
            style={{ color: SUBTEXT }}
          >
            Mid-Market <ArrowRight weight="bold" size={12} /> Fortune 500
            <ArrowRight weight="bold" size={12} /> Fortune 50
          </div>
          <Link
            to="/command"
            data-testid="ascension-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-sm text-[12px] font-mono tracking-[0.16em] uppercase font-bold transition-transform hover:-translate-y-px"
            style={{ background: DEEP_SLATE, color: ALABASTER, border: `1px solid ${DEEP_SLATE}` }}
          >
            Start at Your Tier
            <ArrowRight weight="bold" size={13} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function AscensionCard({ step, index }) {
  const Icon = step.icon;
  return (
    <article
      data-testid={`ascension-card-${step.tier.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="p-7 flex flex-col h-full"
      style={{ background: ALABASTER }}
    >
      <div className="flex items-baseline justify-between mb-6">
        <span
          className="text-[10px] font-mono tracking-[0.22em] uppercase"
          style={{ color: SUBTEXT }}
        >
          {step.rank} · {step.range}
        </span>
        <span
          className="inline-flex items-center justify-center h-9 w-9 rounded-sm"
          style={{ background: DEEP_SLATE, color: ALABASTER }}
        >
          <Icon weight="bold" size={18} />
        </span>
      </div>
      <div
        className="text-[12px] font-mono tracking-[0.18em] uppercase mb-2"
        style={{ color: EX_GREEN }}
      >
        {step.tier}
      </div>
      <h3
        className="font-serif-brand font-semibold text-[22px] leading-tight mb-3"
        style={{ color: DEEP_SLATE }}
      >
        {step.headline}
      </h3>
      <p className="text-[13px] leading-relaxed mb-5" style={{ color: SUBTEXT }}>
        {step.body}
      </p>
      <ul className="mt-auto space-y-1.5 pt-4" style={{ borderTop: `1px solid ${RULE}` }}>
        {step.proof.map((p) => (
          <li key={p} className="flex items-start gap-2 text-[12px]" style={{ color: DEEP_SLATE }}>
            <CheckCircle weight="fill" size={12} style={{ color: EX_GREEN, marginTop: 3 }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      {step.price ? (
        <div
          data-testid={`ascension-price-${step.tier.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="mt-4 pt-3 text-[11px] font-mono tracking-[0.18em] uppercase"
          style={{ color: EX_GREEN, borderTop: `1px dashed ${RULE}` }}
        >
          {step.price}
        </div>
      ) : null}
    </article>
  );
}

/* ─── 6 · Footer ────────────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer
      data-testid="landing-footer"
      className="border-t"
      style={{ borderColor: RULE, background: ALABASTER }}
    >
      <div className="max-w-7xl mx-auto px-8 py-10 flex items-center justify-between flex-wrap gap-4">
        <div className="text-[12px]" style={{ color: SUBTEXT }}>
          © {new Date().getFullYear()} Procureo · Your Strategic Business Partner.
        </div>
        <div
          className="text-[10px] font-mono tracking-[0.22em] uppercase"
          style={{ color: SUBTEXT }}
        >
          Built for the office of the CFO
        </div>
      </div>
    </footer>
  );
}

/* ─── Buyer / User Split · short banded section ─────────────────────────── */
function BuyerUserSplit() {
  return (
    <section
      data-testid="landing-buyer-user-split"
      className="border-t"
      style={{ borderColor: RULE, background: DEEP_SLATE, color: ALABASTER }}
    >
      <div className="max-w-7xl mx-auto px-8 py-14">
        <div
          className="text-[10px] font-mono tracking-[0.22em] uppercase mb-4"
          style={{ color: EX_GREEN }}
        >
          Two Surfaces · One Platform
        </div>
        <h2
          className="font-serif-brand font-semibold leading-tight text-[28px] sm:text-[36px] mb-5"
          style={{ color: ALABASTER, letterSpacing: "-0.015em" }}
        >
          Operated by procurement. Read by the CFO.
        </h2>
        <p
          className="max-w-3xl text-[14.5px] leading-relaxed"
          style={{ color: "rgba(249,250,251,0.78)" }}
        >
          Category leaders run <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> at the workflow level. The CFO consumes the reconciled output — Reinvestment Capital, Revenue Equivalence, PDI coverage — at the dashboard level. One platform. Two surfaces. The buck closes on a named human at the gate.
        </p>
      </div>
    </section>
  );
}

/* ─── Competitive Positioning · category, not company ───────────────────── */
function CompetitivePositioning() {
  const COLS = [
    {
      compares: "Spend analytics platforms",
      claim:    "see what you bought.",
      counter:  "Procureo grades what you should pay.",
    },
    {
      compares: "Market intelligence subscriptions",
      claim:    "read the market.",
      counter:  "Procureo translates the market into your award lines.",
    },
    {
      compares: "Source-to-pay suites",
      claim:    "execute the transaction.",
      counter:  "Procureo decides whether the transaction is worth executing in the first place.",
    },
  ];
  return (
    <section
      id="competitive-positioning"
      data-testid="landing-competitive-positioning"
      className="border-t"
      style={{ borderColor: RULE, background: "#FFFFFF" }}
    >
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div
              className="text-[11px] font-mono tracking-[0.22em] uppercase mb-3"
              style={{ color: EX_GREEN }}
            >
              Positioning
            </div>
            <h2
              className="font-serif-brand font-semibold text-[36px] sm:text-[44px] leading-tight"
              style={{ color: DEEP_SLATE, letterSpacing: "-0.015em" }}
            >
              Procurement intelligence — by category, not by company.
            </h2>
          </div>
          <p
            className="max-w-md text-[14px] leading-relaxed"
            style={{ color: SUBTEXT }}
          >
            <span className="font-extrabold" style={{ color: EX_GREEN }}>PROCUREO</span> sits above all three. The score is one number. The score is auditable. The score is yours.
          </p>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-px"
          style={{ background: RULE }}
          data-testid="positioning-grid"
        >
          {COLS.map((c, i) => (
            <article
              key={c.compares}
              className="p-6 flex flex-col h-full"
              style={{ background: ALABASTER }}
              data-testid={`positioning-card-${i}`}
            >
              <div
                className="text-[10px] font-mono tracking-[0.22em] uppercase font-bold mb-3"
                style={{ color: SUBTEXT }}
              >
                0{i + 1} · Compared with
              </div>
              <div
                className="font-serif-brand font-semibold text-[20px] leading-tight mb-1"
                style={{ color: DEEP_SLATE }}
              >
                {c.compares}
              </div>
              <p className="text-[13px] leading-relaxed" style={{ color: SUBTEXT }}>
                They {c.claim}
              </p>
              <p
                className="mt-4 text-[14px] leading-relaxed font-medium"
                style={{ color: DEEP_SLATE }}
              >
                {c.counter}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Briefing Modal · landing-page conversion CTA ───────────────────────── */
function BriefingModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", spend_band: "", note: "" });
  const [status, setStatus] = useState("IDLE"); // IDLE | SENDING | SENT | ERROR
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setStatus("IDLE");
      setError(null);
      setForm({ name: "", email: "", company: "", spend_band: "", note: "" });
    }
  }, [open]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setStatus("SENDING");
    setError(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${base}/api/contact/briefing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("SENT");
    } catch (err) {
      setStatus("ERROR");
      setError(err.message || "Request failed. Please try again.");
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="briefing-modal"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-sm shadow-2xl"
        style={{ background: ALABASTER, border: `1px solid ${RULE}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: RULE }}
        >
          <div>
            <div className="text-[10px] font-mono tracking-[0.22em] uppercase" style={{ color: EX_GREEN }}>
              Request a Briefing
            </div>
            <div className="font-serif-brand font-semibold text-[18px] mt-1" style={{ color: DEEP_SLATE }}>
              Open a private session.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="briefing-close"
            aria-label="Close"
            className="p-1 rounded-sm hover:bg-black/5"
            style={{ color: SUBTEXT }}
          >
            <X weight="bold" size={16} />
          </button>
        </div>

        {status === "SENT" ? (
          <div className="px-6 py-8 text-center" data-testid="briefing-success">
            <CheckCircle weight="fill" size={36} style={{ color: EX_GREEN }} className="mx-auto mb-4" />
            <div className="font-serif-brand text-[18px] font-semibold mb-2" style={{ color: DEEP_SLATE }}>
              Briefing requested.
            </div>
            <p className="text-[13px] leading-relaxed" style={{ color: SUBTEXT }}>
              A named lead will reach out within one business day to coordinate the session.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[11px] font-mono tracking-[0.16em] uppercase font-bold"
              style={{ background: DEEP_SLATE, color: ALABASTER }}
              data-testid="briefing-success-close"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-3" data-testid="briefing-form">
            <BriefingField label="Name" required value={form.name} onChange={update("name")} testid="briefing-name" />
            <BriefingField label="Work email" required type="email" value={form.email} onChange={update("email")} testid="briefing-email" />
            <BriefingField label="Company" value={form.company} onChange={update("company")} testid="briefing-company" />
            <div>
              <label className="text-[10px] font-mono tracking-[0.18em] uppercase block mb-1" style={{ color: SUBTEXT }}>
                Strategic spend band
              </label>
              <select
                value={form.spend_band}
                onChange={update("spend_band")}
                data-testid="briefing-spend-band"
                className="w-full px-3 py-2 text-[13px] rounded-sm bg-white"
                style={{ border: `1px solid ${RULE}`, color: DEEP_SLATE }}
              >
                <option value="">Select range</option>
                <option value="<$50M">Less than $50M</option>
                <option value="$50M-$500M">$50M – $500M · Mid-Market</option>
                <option value="$500M-$5B">$500M – $5B · Fortune 500</option>
                <option value="$5B+">$5B+ · Fortune 50</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.18em] uppercase block mb-1" style={{ color: SUBTEXT }}>
                Note (optional)
              </label>
              <textarea
                value={form.note}
                onChange={update("note")}
                rows={3}
                data-testid="briefing-note"
                className="w-full px-3 py-2 text-[13px] rounded-sm bg-white resize-none"
                style={{ border: `1px solid ${RULE}`, color: DEEP_SLATE }}
                placeholder="What categories or outcomes should we focus on?"
              />
            </div>

            {error ? (
              <div className="text-[12px] font-mono" style={{ color: "#B91C1C" }} data-testid="briefing-error">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={status === "SENDING"}
              data-testid="briefing-submit"
              className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm text-[12px] font-mono tracking-[0.16em] uppercase font-bold transition-transform hover:-translate-y-px disabled:opacity-60 disabled:hover:translate-y-0"
              style={{ background: EX_GREEN, color: ALABASTER }}
            >
              {status === "SENDING" ? "Sending…" : "Submit Request"}
              {status !== "SENDING" ? <ArrowRight weight="bold" size={13} /> : null}
            </button>
            <p className="text-[10.5px] font-mono mt-1 text-center" style={{ color: SUBTEXT }}>
              Confidential. No marketing list. A named lead will reply.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function BriefingField({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="text-[10px] font-mono tracking-[0.18em] uppercase block mb-1" style={{ color: SUBTEXT }}>
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full px-3 py-2 text-[13px] rounded-sm bg-white"
        style={{ border: `1px solid ${RULE}`, color: DEEP_SLATE }}
      />
    </div>
  );
}
